import { useState, useContext, useRef } from 'react';

import {
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
  Text,
  Spinner,
  useToast,
  useColorModeValue,
  Badge,
  Input,
  Box,
} from '@chakra-ui/react';

import { ChevronDownIcon } from '@chakra-ui/icons';

import { useQuery, useMutation } from '@tanstack/react-query';

import { AuthContext } from 'contexts/AuthContext';

import { apiGetWorkflowUsers, apiAssignLeadToUser } from 'api';

import {
  ROLE_ADMIN,
  ROLE_MANAGER,
  ROLE_BROKER_PARTNER,
} from 'variables/constants';

import type { APILeadsListResultData, APIUserData } from 'types/api.types';

interface LeadAssignUserProps {
  lead: APILeadsListResultData;
  onSuccess: (user: APIUserData, brokerId: number | 'me') => void;
  size?: 'sm' | 'md' | 'lg';
}

const LeadAssignUser = ({
  lead,
  onSuccess,
  size = 'sm',
}: LeadAssignUserProps) => {
  const { user } = useContext(AuthContext);
  const toast = useToast();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [assignedToUser, setAssignedToUser] = useState<APIUserData | null>(
    null,
  );
  const [assignedToExistingUser, setAssignedToExistingUser] =
    useState<APIUserData | null>(null);

  const dropdownButtonBackground = useColorModeValue('gray.100', 'gray.700');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const {
    data: availableUsers,
    isLoading: loadingUsers,
    error: usersError,
  } = useQuery({
    queryKey: ['lead-users', lead.workflow?.id],
    queryFn: () => apiGetWorkflowUsers(lead.workflow?.id),
    enabled: isMenuOpen,
    staleTime: 5 * 60 * 1000,
  });

  const handleMenuOpen = () => {
    setIsMenuOpen(true);
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
    setSearchQuery('');
  };

  const filteredUsers = availableUsers?.filter((broker: APIUserData) => {
    const fullName = `${broker.first_name} ${broker.last_name}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase());
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (brokerId: number | 'me') =>
      apiAssignLeadToUser(lead.id, brokerId),
    onSuccess: (data, variables) => {
      toast({
        title: 'Lead assigned',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      setAssignedToUser(data);
      onSuccess(data, variables);
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to assign lead',
        description:
          error?.detail || 'An error occurred while assigning the lead.',
        status: 'error',
        duration: 10000,
        isClosable: true,
        position: 'top',
      });
      if (error?.user) {
        setAssignedToExistingUser(error.user);
        onSuccess(error.user, null);
      }
    },
  });

  const onBrokerAssign = (brokerId?: number) => {
    mutate(brokerId || 'me');
  };

  if (assignedToExistingUser) {
    return (
      <Badge
        alignContent="center"
        colorScheme="red"
        w="100%"
        textAlign="center"
        h={size === 'md' ? '40px' : '32px'}
      >
        Already assigned to{' '}
        {`${assignedToExistingUser.first_name} ${assignedToExistingUser.last_name}`}
      </Badge>
    );
  }

  if (assignedToUser) {
    return (
      <Badge
        alignContent="center"
        colorScheme="green"
        w="100%"
        textAlign="center"
        h={size === 'md' ? '40px' : '32px'}
      >
        Assigned to {assignedToUser.first_name} {assignedToUser.last_name}
      </Badge>
    );
  }

  if (![ROLE_ADMIN, ROLE_MANAGER, ROLE_BROKER_PARTNER].includes(user.role)) {
    return (
      <Button
        size={size}
        colorScheme="yellow"
        onClick={(e) => {
          e.stopPropagation();
          onBrokerAssign();
        }}
        isLoading={isPending}
        loadingText="Assigning"
        width="200px"
      >
        Assign to me
      </Button>
    );
  }

  return (
    <>
      <Button
        size={size}
        colorScheme="yellow"
        onClick={(e) => {
          e.stopPropagation();
          onBrokerAssign();
        }}
        isLoading={isPending}
        loadingText="Assigning"
      >
        Assign to me
      </Button>

      <Menu
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
        placement="bottom-end"
        strategy="fixed"
      >
        <MenuButton
          as={Button}
          rightIcon={<ChevronDownIcon />}
          size={size}
          isDisabled={isPending}
          onClick={(e) => {
            e.stopPropagation();
          }}
          _hover={{
            background: dropdownButtonBackground,
          }}
          variant="outline"
          borderColor="gray.300"
        >
          Assign to broker
        </MenuButton>
        <MenuList maxH="400px" overflowY="auto" overflowX="hidden">
          <Box px={3} pb={2} position="sticky" top={0} bg="inherit" zIndex={1}>
            <Input
              ref={searchInputRef}
              placeholder="Search brokers..."
              size="sm"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setTimeout(() => searchInputRef.current?.focus(), 0);
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </Box>
          {loadingUsers ? (
            <MenuItem closeOnSelect={false}>
              <Spinner size="sm" mr={2} /> Loading users...
            </MenuItem>
          ) : usersError ? (
            <MenuItem closeOnSelect={false}>Failed to load users</MenuItem>
          ) : !filteredUsers || filteredUsers.length === 0 ? (
            <MenuItem closeOnSelect={false}>
              {searchQuery ? 'No matching users' : 'No users available'}
            </MenuItem>
          ) : (
            filteredUsers.map((broker: APIUserData) => (
              <MenuItem
                key={broker.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onBrokerAssign(broker.id);
                }}
                isDisabled={isPending}
              >
                <Flex direction="column">
                  <Text>
                    {broker.first_name} {broker.last_name}
                  </Text>
                </Flex>
              </MenuItem>
            ))
          )}
        </MenuList>
      </Menu>
    </>
  );
};

export default LeadAssignUser;
